/**
 * The team hub at /home (Proposal 09 § 8.2,
 * ~/Vault/_system/proposals/09-elevation-separation.md).
 *
 * Internal Priorities.ai material for the team (Wes and Harry; see
 * lib/elevationAccess.ts), never shown to customers: RequireElevationAccess
 * sends anyone else to the dashboard. /home used to reach Mission Control,
 * which is now the owner's alone, so the team needs its own way in. The link
 * to Elevation itself is drawn only for the owner.
 *
 * Built only from the Elevation primitives (components/elevation).
 */

import { Link } from 'react-router-dom';
import { Card, Heading, PageShell, Section, SectionLabel } from '../components/elevation';
import { useIsElevationOwner } from '../lib/elevationAccess';

interface Area {
  title: string;
  says: string;
  links: { to: string; label: string }[];
  note?: { before: string; to: string; label: string };
}

const AREAS: Area[] = [
  {
    title: 'Design Language',
    says: 'How Priorities.ai looks and speaks: tokens, colors, type, logo, components.',
    links: [{ to: '/elevation/design-language', label: 'Design Language' }],
    note: {
      before: 'The older design pages are kept, marked legacy: ',
      to: '/elevation/archive/design-language-legacy',
      label: 'Legacy design pages',
    },
  },
  {
    title: 'Product Lab',
    says: 'Lab tools tied to the product.',
    links: [{ to: '/elevation/lab', label: 'The Lab' }],
  },
  {
    // Kept in Priorities.ai as team pages, at their old addresses (Wes,
    // 2026-10-18; Proposal 09 § 8.2, "Mental models").
    title: 'Mental models',
    says: 'The models behind the method, their M1 and M2 forms, and the Field of Play.',
    links: [
      { to: '/elevation/ladder-of-inference', label: 'Ladder of Inference' },
      { to: '/elevation/ladder-of-inference-m1', label: 'Ladder of Inference (M1)' },
      { to: '/elevation/ladder-of-inference-m2-model', label: 'Ladder of Inference (M2 model)' },
      { to: '/elevation/ladder-of-inference-demo', label: 'Ladder of Inference demo' },
      { to: '/elevation/johari-window', label: 'Johari Window' },
      { to: '/elevation/johari-window-m2-model', label: 'Johari Window (M2 model)' },
      { to: '/elevation/logical-levels', label: 'Logical Levels' },
      { to: '/elevation/logical-levels-m2-model', label: 'Logical Levels (M2 model)' },
      { to: '/elevation/inquiry-sequence', label: 'Inquiry Sequence' },
      { to: '/elevation/bounded-agency-delegation', label: 'Bounded Agency' },
      { to: '/elevation/field-of-play', label: 'Field of Play' },
      { to: '/elevation/island', label: 'Island' },
      { to: '/elevation/mountain', label: 'Mountain' },
    ],
  },
  {
    title: 'Career development',
    says: "What's next, and the tools that go with it.",
    links: [{ to: '/apps/whats-next', label: "What's Next" }],
  },
  {
    title: 'World',
    says: 'The Peak world.',
    links: [{ to: '/world', label: 'World' }],
  },
  {
    title: 'Method and Workbook',
    says: 'The method behind the app, and its workbook.',
    links: [
      { to: '/elevation/method', label: 'The Method' },
      { to: '/elevation/workbook', label: 'The Workbook' },
    ],
  },
  {
    title: 'Product records',
    says: 'The product roadmap.',
    links: [{ to: '/home/roadmap', label: 'Roadmap' }],
  },
];

const LINK_CLASS =
  'font-body text-sm font-medium text-[var(--p-ink)] underline underline-offset-4 decoration-[var(--p-hairline-2)] hover:decoration-[var(--p-ink)]';

function AreaCard({ area }: { area: Area }) {
  return (
    <Card>
      <Heading as="h2" size="md">
        {area.title}
      </Heading>
      <p className="mt-1 text-sm text-[var(--p-ink-soft)]">{area.says}</p>
      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2">
        {area.links.map((l) => (
          <Link key={l.to} to={l.to} className={LINK_CLASS}>
            {l.label}
          </Link>
        ))}
      </div>
      {area.note && (
        <p className="mt-3 text-xs text-[var(--p-ink-soft)]">
          {area.note.before}
          <Link to={area.note.to} className={LINK_CLASS}>
            {area.note.label}
          </Link>
          .
        </p>
      )}
    </Card>
  );
}

export function TeamHome() {
  const elevationOwner = useIsElevationOwner();

  return (
    <PageShell>
      <Link
        to="/prioritization/dashboard"
        className="font-body text-[11px] font-medium uppercase tracking-[0.28em] text-[var(--p-label)] hover:text-[var(--p-ink)]"
      >
        ← Priorities.ai
      </Link>

      <Section className="mt-8">
        <SectionLabel>Peak Priorities · internal</SectionLabel>
        <Heading as="h1" size="xl">
          Team home
        </Heading>
        <p className="mt-2 text-[15px] text-[var(--p-ink-soft)]">
          Internal material for the Peak Priorities team. Customers never see these pages.
        </p>
      </Section>

      <Section>
        <div className="grid gap-4 sm:grid-cols-2">
          {AREAS.map((area) => (
            <AreaCard key={area.title} area={area} />
          ))}
          {elevationOwner && (
            <AreaCard
              area={{
                title: 'Elevation',
                says: 'Your knowledge system. Only you see this.',
                links: [{ to: '/elevation/home', label: 'Elevation' }],
              }}
            />
          )}
        </div>
      </Section>
    </PageShell>
  );
}

export default TeamHome;
